import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp, faArrowDown, faChartBar, faUserTie, faUsers, faTags, faSearch, faPlusCircle } from "@fortawesome/free-solid-svg-icons";

import Container from "../../components/UI/Container/Container";
import Spinner from "../../components/UI/Spinner/Spinner";
import Button, { EButtonColor } from "../../components/UI/Button/Button";
import Alert from "../../components/UI/Alert/Alert";
import SearchSelect, { IOption } from "../../components/UI/SearchSelect/SearchSelect";
import { useAuthUser } from "../../hooks/useAuthUser";
import { IAppState } from "../../interfaces";
import * as actions from "../../store/actions";
import { getReportData, IReportResponse } from "../../services/reportsService";
import { ETranslation } from "../../translations/translation-keys";
import classes from "./Reports.module.scss";

interface ISavedSearch {
  id: string;
  name: string;
  services: IOption[];
  weeks: number;
}

interface IProps extends RouteComponentProps {}

const Reports: React.FC<IProps> = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const authUser = useAuthUser();

  const { services, loadingServices } = useSelector((state: IAppState) => ({
    services: state.services.services,
    loadingServices: state.services.loading
  }));

  const [selectedServices, setSelectedServices] = useState<IOption[]>([]);
  const [weeks, setWeeks] = useState<number>(4);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<IReportResponse | null>(null);
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);
  const [hoveredAttributionKey, setHoveredAttributionKey] = useState<string | null>(null);
  const [hoveredProviderIndex, setHoveredProviderIndex] = useState<number | null>(null);

  // Tallennetut hakuehdot (localStorage)
  const [savedSearches, setSavedSearches] = useState<ISavedSearch[]>([]);
  const [selectedSavedSearchId, setSelectedSavedSearchId] = useState<string>("");

  // Lataa tallennetut haut alussa
  useEffect(() => {
    try {
      const stored = localStorage.getItem("hankix_saved_reports_searches");
      if (stored) {
        setSavedSearches(JSON.parse(stored));
      }
    } catch (e) {
      console.error("Virhe ladattaessa tallennettuja hakuja localStoragesta:", e);
    }
  }, []);

  // Käyttöoikeudet
  const isServiceProvider = authUser?.organization?.level === "SERVICE_PROVIDER";

  // Lataa palvelut alussa
  useEffect(() => {
    if (!services) {
      dispatch(actions.listServices());
    }
  }, [dispatch, services]);

  // Suodatetaan näytettävät palvelut käyttäjän oikeuksien mukaan
  const filteredServices = useMemo(() => {
    if (!services) return [];
    if (isServiceProvider && authUser?.organization?.services) {
      return services.filter(s => authUser.organization.services!.some(as => as.id === s.id));
    }
    return services;
  }, [services, isServiceProvider, authUser]);

  // Hae raporttitiedot
  const fetchData = useCallback((servicesToFetch = selectedServices, weeksToFetch = weeks) => {
    if (servicesToFetch && servicesToFetch.length > 0) {
      setLoading(true);
      setError(null);
      const serviceIds = servicesToFetch.map((s: any) => s.value);
      getReportData(serviceIds, undefined, weeksToFetch)
        .then(res => {
          if (res) {
            setReportData(res);
          } else {
            setError("Raporttitietojen haku epäonnistui taustajärjestelmästä.");
          }
          setLoading(false);
        })
        .catch(err => {
          console.error(err);
          setError("Tietokantavirhe tai puuttuva yhteys taustajärjestelmään.");
          setLoading(false);
        });
    } else {
      setReportData(null);
    }
  }, [selectedServices, weeks]);

  // Tallentaa nykyiset hakuehdot localstorageen
  const handleSaveSearch = () => {
    let weekPrefix = "12vko";
    if (weeks === 4) weekPrefix = "4vko";
    else if (weeks === 24) weekPrefix = "6kk";
    else if (weeks === 52) weekPrefix = "12kk";

    let name = `${weekPrefix} Ei palveluita`;
    if (selectedServices.length === 1) {
      name = `${weekPrefix} ${selectedServices[0].label}`;
    } else if (selectedServices.length > 1) {
      name = `${weekPrefix} ${selectedServices.length} palvelua`;
    }

    const newSearch: ISavedSearch = {
      id: Date.now().toString(),
      name,
      services: selectedServices,
      weeks
    };

    const updated = [...savedSearches, newSearch];
    setSavedSearches(updated);
    localStorage.setItem("hankix_saved_reports_searches", JSON.stringify(updated));
    setSelectedSavedSearchId(newSearch.id);
  };

  // Lataa tallennetun haun tiedot suoraan käyttöön (onChange latauksella)
  const handleSelectSavedSearch = (id: string) => {
    setSelectedSavedSearchId(id);
    if (!id) return;
    const match = savedSearches.find(s => s.id === id);
    if (match) {
      setSelectedServices(match.services);
      setWeeks(match.weeks);
      fetchData(match.services, match.weeks);
    }
  };

  // Poistaa tallennetun haun localstoragesta
  const handleDeleteSavedSearch = (id: string) => {
    const updated = savedSearches.filter(s => s.id !== id);
    setSavedSearches(updated);
    localStorage.setItem("hankix_saved_reports_searches", JSON.stringify(updated));
    if (selectedSavedSearchId === id) {
      setSelectedSavedSearchId("");
    }
  };

  // Valitsee kaikki tarjolla olevat palvelut suodattimeen kerralla
  const handleSelectAllServices = () => {
    const allOptions = filteredServices.map((s: any) => ({
      value: s.id,
      label: s.name
    }));
    setSelectedServices(allOptions);
  };

  // Aseta oletuspalvelut ja tee ensihaku kun palvelut on ladattu
  useEffect(() => {
    if (filteredServices && filteredServices.length > 0 && selectedServices.length === 0) {
      const initialOptions = filteredServices.map((s: any) => ({
        value: s.id,
        label: s.name
      }));
      setSelectedServices(initialOptions);
      fetchData(initialOptions, weeks);
    }
  }, [filteredServices, selectedServices.length, weeks, fetchData]);

  // --- SVG Kuvaajalaskennat (Custom Interactive SVG Charts) ---

  // A. Liidit & Kasvutrendi (Bar & Line Combo)
  const comboChartDimensions = { width: 700, height: 300, paddingLeft: 50, paddingRight: 50, paddingTop: 30, paddingBottom: 45 };
  const comboData = useMemo(() => reportData?.timeSeries || [], [reportData?.timeSeries]);

  const maxLeads = useMemo(() => {
    if (comboData.length === 0) return 10;
    return Math.max(...comboData.map(d => d.leads), 1);
  }, [comboData]);

  const maxCumulative = useMemo(() => {
    if (comboData.length === 0) return 100;
    return Math.max(...comboData.map(d => d.cumulativeLeads), 1);
  }, [comboData]);

  // B. Hankintakanavat (Donut Chart)
  const donutDimensions = { size: 240, radius: 100, innerRadius: 65, center: 120 };
  const attributionData = useMemo(() => reportData?.attribution || { DIRECT: 0, AI: 0, PARTNERS: 0, SEARCH: 0, OTHER: 0 }, [reportData?.attribution]);
  const totalAttributed = Object.values(attributionData).reduce((a, b) => a + b, 0);

  const donutSectors = useMemo(() => {
    let accumulatedAngle = 0;
    return Object.entries(attributionData).map(([key, value]) => {
      const percentage = totalAttributed > 0 ? (value / totalAttributed) : 0;
      const angle = percentage * 360;
      const startAngle = accumulatedAngle;
      accumulatedAngle += angle;
      return {
        key,
        value,
        percentage,
        startAngle,
        endAngle: accumulatedAngle
      };
    });
  }, [attributionData, totalAttributed]);

  const getDonutPath = (startAngle: number, endAngle: number) => {
    if (endAngle - startAngle >= 359.99) {
      // Piirretään täysi rengas
      return `M ${donutDimensions.center} ${donutDimensions.center - donutDimensions.radius} 
              A ${donutDimensions.radius} ${donutDimensions.radius} 0 1 1 ${donutDimensions.center - 0.01} ${donutDimensions.center - donutDimensions.radius}
              M ${donutDimensions.center} ${donutDimensions.center - donutDimensions.innerRadius}
              A ${donutDimensions.innerRadius} ${donutDimensions.innerRadius} 0 1 0 ${donutDimensions.center - 0.01} ${donutDimensions.center - donutDimensions.innerRadius}`;
    }

    const rad = Math.PI / 180;
    // Kulmien korjaus jotta 0-kulma on ylhäällä
    const sAngle = startAngle - 90;
    const eAngle = endAngle - 90;

    const x1 = donutDimensions.center + donutDimensions.radius * Math.cos(sAngle * rad);
    const y1 = donutDimensions.center + donutDimensions.radius * Math.sin(sAngle * rad);
    const x2 = donutDimensions.center + donutDimensions.radius * Math.cos(eAngle * rad);
    const y2 = donutDimensions.center + donutDimensions.radius * Math.sin(eAngle * rad);

    const x3 = donutDimensions.center + donutDimensions.innerRadius * Math.cos(eAngle * rad);
    const y3 = donutDimensions.center + donutDimensions.innerRadius * Math.sin(eAngle * rad);
    const x4 = donutDimensions.center + donutDimensions.innerRadius * Math.cos(sAngle * rad);
    const y4 = donutDimensions.center + donutDimensions.innerRadius * Math.sin(sAngle * rad);

    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${x1} ${y1} 
            A ${donutDimensions.radius} ${donutDimensions.radius} 0 ${largeArc} 1 ${x2} ${y2}
            L ${x3} ${y3}
            A ${donutDimensions.innerRadius} ${donutDimensions.innerRadius} 0 ${largeArc} 0 ${x4} ${y4} Z`;
  };

  const attributionColors: Record<string, string> = {
    DIRECT: "#8b5cf6",  // Violetti
    AI: "#06b6d4",      // Turkoosi (Gemini/AI vibe)
    PARTNERS: "#10b981",// Vihreä (Kumppanit)
    SEARCH: "#3b82f6",  // Sininen (Haku)
    OTHER: "#f59e0b"    // Oranssi
  };

  const attributionLabels: Record<string, string> = {
    DIRECT: "Suora liikenne",
    AI: "Tekoäly (AI)",
    PARTNERS: "Kumppanit",
    SEARCH: "Hakukoneet",
    OTHER: "Muut / Somelinkit"
  };

  // C. Yrittäjien Ansaintatyyppien jakaumakehitys (Stacked Bar Chart) - Vain Operator/Admin
  const providerChartDimensions = { width: 700, height: 300, paddingLeft: 50, paddingRight: 40, paddingTop: 30, paddingBottom: 45 };
  const maxProviders = useMemo(() => {
    if (comboData.length === 0) return 10;
    return Math.max(...comboData.map(d => d.subscriptionProviders + d.microProviders), 1);
  }, [comboData]);

  // Loading spinner
  if (loadingServices) {
    return (
      <Container style={{ textAlign: "center", padding: "3rem 0" }}>
        <Spinner />
      </Container>
    );
  }

  return (
    <>
      {error && <Alert children={error} />}
      
      <div className={classes.DashboardContainer}>
        {/* Yläpalkki ja suodattimet */}
        <div className={classes.DashboardHeader}>
          <div>
            <h1>{t(ETranslation.REPORTS) || "Raportointi"}</h1>
            <p className={classes.Subtitle}>
              {isServiceProvider 
                ? "Yrityksellesi tilattujen palveluiden yleinen aktiviteettikehitys Hankixissa." 
                : "Seuraa tarjouspyyntö virtaa, liikenteen lähteitä ja käyttäjäaktiviteettia."}
            </p>
          </div>

          <div className={classes.FilterRow}>
            {/* Palveluvalitsin */}
            <div className={classes.FilterControl} style={{ minWidth: "260px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <label>{t(ETranslation.COMMON_SERVICE) || "Palvelu"}</label>
                <button
                  onClick={handleSelectAllServices}
                  title="Lisää kaikki"
                  style={{
                    background: "none",
                    border: "none",
                    color: "#8b5cf6",
                    cursor: "pointer",
                    padding: "0 0 0.25rem 0",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "0.85rem",
                    transition: "color 0.2s ease"
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#6d28d9")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#8b5cf6")}
                >
                  <FontAwesomeIcon icon={faPlusCircle} />
                </button>
              </div>
              <SearchSelect
                inputName="serviceSelect"
                placeholder="Valitse palvelut..."
                multiple={true}
                options={filteredServices.map((s) => ({ value: s.id, label: s.name }))}
                value={selectedServices}
                onChange={(val) => setSelectedServices(val as IOption[])}
              />
            </div>

            {/* Viikkovalitsin */}
            <div className={classes.FilterControl}>
              <label htmlFor="weeksSelect">Aikarajaus</label>
              <select
                id="weeksSelect"
                value={weeks}
                onChange={(e) => setWeeks(Number(e.target.value))}
              >
                <option value={4}>Viimeiset 4 viikkoa / 30 vrk (Vakio)</option>
                <option value={12}>Viimeiset 12 viikkoa</option>
                <option value={24}>Viimeiset 6 kuukautta</option>
                <option value={52}>Viimeiset 12 kuukautta</option>
              </select>
            </div>

            {/* Hae-painike */}
            <div className={classes.FilterControl}>
              <label style={{ visibility: "hidden" }}>Hae</label>
              <Button
                onClick={() => fetchData()}
                disabled={selectedServices.length === 0 || loading}
                loading={loading}
                icon={faSearch}
              >
                Hae
              </Button>
            </div>

            {/* Tallenna haku -painike */}
            <div className={classes.FilterControl}>
              <label style={{ visibility: "hidden" }}>Tallenna</label>
              <Button
                onClick={handleSaveSearch}
                disabled={selectedServices.length === 0}
                color={EButtonColor.SECONDARY}
              >
                Tallenna haku
              </Button>
            </div>

            {/* Tallennetut haut -valitsin */}
            <div className={classes.FilterControl} style={{ minWidth: "220px" }}>
              <label htmlFor="savedSearchesSelect">Tallennetut haut</label>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <select
                  id="savedSearchesSelect"
                  value={selectedSavedSearchId}
                  onChange={(e) => handleSelectSavedSearch(e.target.value)}
                  style={{ flexGrow: 1 }}
                >
                  <option value="">Valitse tallennettu haku...</option>
                  {savedSearches.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {selectedSavedSearchId && (
                  <button
                    onClick={() => handleDeleteSavedSearch(selectedSavedSearchId)}
                    title="Poista tallennettu haku"
                    style={{
                      background: "none",
                      border: "none",
                      color: "#ef4444",
                      cursor: "pointer",
                      fontSize: "1.1rem",
                      padding: "0 0.25rem",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "5rem 0" }}>
            <Spinner />
          </div>
        ) : reportData ? (
          <>
            {/* KPI avainlukukortit lasiefektillä (Glassmorphic KPI Cards) */}
            <div className={classes.KpiGrid}>
              
              {/* Tämän viikon tarjouspyynnöt */}
              <div className={classes.KpiCard}>
                <div className={classes.CardHeader}>
                  <span>Tarjouspyynnöt (viikko)</span>
                  <div className={classes.IconContainer} style={{ background: "rgba(139, 92, 246, 0.15)", color: "#8b5cf6" }}>
                    <FontAwesomeIcon icon={faChartBar} />
                  </div>
                </div>
                <div className={classes.CardBody}>
                  <div className={classes.Number}>{reportData.summary.currentWeekLeads}</div>
                  <div className={`${classes.Trend} ${reportData.summary.leadChangePercent >= 0 ? classes.Positive : classes.Negative}`}>
                    <FontAwesomeIcon icon={reportData.summary.leadChangePercent >= 0 ? faArrowUp : faArrowDown} />
                    <span>{Math.abs(reportData.summary.leadChangePercent).toFixed(1)}%</span>
                    <span className={classes.TrendLabel}>ed. viikkoon</span>
                  </div>
                </div>
              </div>

              {/* Kumulatiiviset tarjouspyynnöt */}
              <div className={classes.KpiCard}>
                <div className={classes.CardHeader}>
                  <span>Tarjouspyynnöt Yhteensä (jaksolla)</span>
                  <div className={classes.IconContainer} style={{ background: "rgba(6, 182, 212, 0.15)", color: "#06b6d4" }}>
                    <FontAwesomeIcon icon={faTags} />
                  </div>
                </div>
                <div className={classes.CardBody}>
                  <div className={classes.Number}>{reportData.summary.totalLeads}</div>
                  <div className={classes.InfoLabel}>Kerättyjä tarjouspyyntöjä</div>
                </div>
              </div>

              {/* Aktiiviset kumppanit */}
              <div className={classes.KpiCard}>
                <div className={classes.CardHeader}>
                  <span>Palveluntarjoajat</span>
                  <div className={classes.IconContainer} style={{ background: "rgba(16, 185, 129, 0.15)", color: "#10b981" }}>
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                </div>
                <div className={classes.CardBody}>
                  <div className={classes.Number}>{reportData.summary.activeProviders}</div>
                  <div className={classes.InfoLabel}>Aktiivisia yrityksiä alueella</div>
                </div>
              </div>

              {/* Laskutusprofiili (vain Primary/Operator) tai Aktiviteettiluku (Service Provider) */}
              {!isServiceProvider ? (
                <div className={classes.KpiCard}>
                  <div className={classes.CardHeader}>
                    <span>Laskutusprofiili</span>
                    <div className={classes.IconContainer} style={{ background: "rgba(245, 158, 11, 0.15)", color: "#f59e0b" }}>
                      <FontAwesomeIcon icon={faUserTie} />
                    </div>
                  </div>
                  <div className={classes.CardBody}>
                    <div className={classes.NumberSplit}>
                      <div>
                        <span className={classes.SmallVal}>{reportData.summary.subscriptionPercent.toFixed(0)}%</span>
                        <span className={classes.LabelVal}>Kk-maksu</span>
                      </div>
                      <div className={classes.Divider} />
                      <div>
                        <span className={classes.SmallVal}>{reportData.summary.microPercent.toFixed(0)}%</span>
                        <span className={classes.LabelVal}>Mikro</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={classes.KpiCard}>
                  <div className={classes.CardHeader}>
                    <span>Aktiviteetti yhteensä</span>
                    <div className={classes.IconContainer} style={{ background: "rgba(245, 158, 11, 0.15)", color: "#f59e0b" }}>
                      <FontAwesomeIcon icon={faUserTie} />
                    </div>
                  </div>
                  <div className={classes.CardBody}>
                    <div className={classes.Number}>
                      {comboData.reduce((acc, curr) => acc + curr.totalActivity, 0)}
                    </div>
                    <div className={classes.InfoLabel}>Tapahtumia järjestelmässä</div>
                  </div>
                </div>
              )}

            </div>

            {/* Kaaviograafit */}
            <div className={classes.ChartsGrid}>
              
              {/* 1. Liidikehitys & Kasvutrendi (Yhdistetty kaavio) */}
              <div className={classes.ChartCard}>
                <h3>Tarjouspyyntöjen virta ja kumulatiivinen kehitys</h3>
                <div className={classes.SvgWrapper}>
                  <svg 
                    viewBox={`0 0 ${comboChartDimensions.width} ${comboChartDimensions.height}`}
                    className={classes.InteractiveSvg}
                  >
                    <defs>
                      <linearGradient id="leadsBarGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.85" />
                        <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.1" />
                      </linearGradient>
                      <linearGradient id="cumLineGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#06b6d4" />
                        <stop offset="100%" stopColor="#3b82f6" />
                      </linearGradient>
                      <filter id="glowFilter" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur stdDeviation="4" result="blur" />
                        <feComposite in="SourceGraphic" in2="blur" operator="over" />
                      </filter>
                    </defs>

                    {/* Taustaviivat (Akselit) */}
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
                      const y = comboChartDimensions.paddingTop + 
                        (comboChartDimensions.height - comboChartDimensions.paddingTop - comboChartDimensions.paddingBottom) * ratio;
                      return (
                        <line 
                          key={index}
                          x1={comboChartDimensions.paddingLeft}
                          y1={y}
                          x2={comboChartDimensions.width - comboChartDimensions.paddingRight}
                          y2={y}
                          stroke="rgba(255,255,255,0.06)"
                          strokeWidth="1"
                        />
                      );
                    })}

                    {/* Weekly Bars (Liidit) */}
                    {comboData.map((d, index) => {
                      const totalWeeks = comboData.length;
                      const chartWidth = comboChartDimensions.width - comboChartDimensions.paddingLeft - comboChartDimensions.paddingRight;
                      const colWidth = chartWidth / totalWeeks;
                      
                      const x = comboChartDimensions.paddingLeft + colWidth * index + (colWidth * 0.15);
                      const barWidth = colWidth * 0.7;
                      
                      const chartHeight = comboChartDimensions.height - comboChartDimensions.paddingTop - comboChartDimensions.paddingBottom;
                      const leadsHeight = (d.leads / maxLeads) * chartHeight;
                      const y = comboChartDimensions.height - comboChartDimensions.paddingBottom - leadsHeight;

                      return (
                        <g 
                          key={index} 
                          onMouseEnter={() => setHoveredBarIndex(index)}
                          onMouseLeave={() => setHoveredBarIndex(null)}
                          style={{ cursor: "pointer" }}
                        >
                          {/* Palkki */}
                          <rect 
                            x={x}
                            y={y}
                            width={barWidth}
                            height={Math.max(leadsHeight, 1)}
                            fill="url(#leadsBarGrad)"
                            rx={3}
                            className={classes.BarElement}
                            style={{ 
                              transition: "all 0.3s ease",
                              fillOpacity: hoveredBarIndex === index ? 1 : 0.8
                            }}
                          />
                          {/* Viikon tekstiakseli */}
                          {index % (totalWeeks > 15 ? 2 : 1) === 0 && (
                            <text 
                              x={x + barWidth/2}
                              y={comboChartDimensions.height - 15}
                              textAnchor="middle"
                              fill="#888"
                              fontSize="11"
                            >
                              {d.weekLabel}
                            </text>
                          )}
                        </g>
                      );
                    })}

                    {/* Cumulative Line (Kasvukäyrä) */}
                    {comboData.length > 1 && (() => {
                      const totalWeeks = comboData.length;
                      const chartWidth = comboChartDimensions.width - comboChartDimensions.paddingLeft - comboChartDimensions.paddingRight;
                      const colWidth = chartWidth / totalWeeks;
                      const chartHeight = comboChartDimensions.height - comboChartDimensions.paddingTop - comboChartDimensions.paddingBottom;

                      const points = comboData.map((d, index) => {
                        const x = comboChartDimensions.paddingLeft + colWidth * index + (colWidth / 2);
                        const y = comboChartDimensions.height - comboChartDimensions.paddingBottom - ((d.cumulativeLeads / maxCumulative) * chartHeight);
                        return { x, y };
                      });

                      // Piirretään viiva
                      const pathD = points.reduce((acc, curr, index) => {
                        if (index === 0) return `M ${curr.x} ${curr.y}`;
                        // Lisätään loiva kaarevuus viivalle
                        const prev = points[index - 1];
                        const cpX1 = prev.x + (curr.x - prev.x) / 3;
                        const cpY1 = prev.y;
                        const cpX2 = prev.x + 2 * (curr.x - prev.x) / 3;
                        const cpY2 = curr.y;
                        return `${acc} C ${cpX1} ${cpY1}, ${cpX2} ${cpY2}, ${curr.x} ${curr.y}`;
                      }, "");

                      return (
                        <>
                          {/* Varjo/Hehkuviiva taustalle */}
                          <path 
                            d={pathD}
                            fill="none"
                            stroke="rgba(6, 182, 212, 0.4)"
                            strokeWidth="5"
                            filter="url(#glowFilter)"
                          />
                          {/* Ydinviiva */}
                          <path 
                            d={pathD}
                            fill="none"
                            stroke="url(#cumLineGrad)"
                            strokeWidth="3.5"
                          />
                          {/* Interaktiiviset ympyrät viivalla */}
                          {points.map((pt, idx) => (
                            <circle 
                              key={idx}
                              cx={pt.x}
                              cy={pt.y}
                              r={hoveredBarIndex === idx ? 6 : 3.5}
                              fill="#fff"
                              stroke="#06b6d4"
                              strokeWidth="2.5"
                              style={{ transition: "all 0.2s ease" }}
                            />
                          ))}
                        </>
                      );
                    })()}

                    {/* Y-akselien arvot */}
                    <text x={10} y={comboChartDimensions.paddingTop + 10} fill="#888" fontSize="10" fontWeight="bold">Liidit (viikko)</text>
                    <text x={comboChartDimensions.width - 5} y={comboChartDimensions.paddingTop + 10} textAnchor="end" fill="#888" fontSize="10" fontWeight="bold">Yhteensä</text>
                  </svg>
                </div>

                {/* Siisti, dynaaminen Tooltip/Tietolaatikko palkin kohdistukselle */}
                <div className={classes.ChartTooltipBox}>
                  {hoveredBarIndex !== null ? (
                    <div className={classes.TooltipContent}>
                      <span className={classes.TooltipWeek}>{comboData[hoveredBarIndex].weekLabel} ({comboData[hoveredBarIndex].startDate})</span>
                      <div className={classes.TooltipRows}>
                        <div><strong>Viikon tarjouspyynnöt:</strong> <span style={{ color: "#8b5cf6", fontWeight: "bold" }}>{comboData[hoveredBarIndex].leads} kpl</span></div>
                        <div><strong>Kumulatiivinen kokonaismäärä:</strong> <span style={{ color: "#06b6d4", fontWeight: "bold" }}>{comboData[hoveredBarIndex].cumulativeLeads} kpl</span></div>
                        {comboData[hoveredBarIndex].totalActivity > 0 && (
                          <div><strong>Aktiviteettiloki:</strong> <span style={{ color: "#f59e0b", fontWeight: "bold" }}>{comboData[hoveredBarIndex].totalActivity} kpl</span></div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className={classes.TooltipPlaceholder}>
                      Vie hiiri kuvaajan palkkien päälle nähdäksesi viikkokohtaiset tiedot.
                    </div>
                  )}
                </div>
              </div>

              {/* 2. Liikenteen hankintalähteet (Source Attribution Donut) */}
              <div className={classes.ChartCard}>
                <h3>Liikenteen hankintakanavat (Attribuutio)</h3>
                
                {totalAttributed > 0 ? (
                  <div className={classes.DonutLayout}>
                    <div className={classes.DonutWrapper}>
                      <svg viewBox={`0 0 ${donutDimensions.size} ${donutDimensions.size}`} className={classes.InteractiveSvg}>
                        {donutSectors.map((sector) => {
                          if (sector.value === 0) return null;
                          const isHovered = hoveredAttributionKey === sector.key;
                          
                          return (
                            <path 
                              key={sector.key}
                              d={getDonutPath(sector.startAngle, sector.endAngle)}
                              fill={attributionColors[sector.key]}
                              fillOpacity={hoveredAttributionKey ? (isHovered ? 1 : 0.4) : 0.85}
                              style={{ 
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                transform: isHovered ? "scale(1.03)" : "scale(1)",
                                transformOrigin: `${donutDimensions.center}px ${donutDimensions.center}px`
                              }}
                              onMouseEnter={() => setHoveredAttributionKey(sector.key)}
                              onMouseLeave={() => setHoveredAttributionKey(null)}
                            />
                          );
                        })}
                        {/* Keskiaukon teksti */}
                        <circle cx={donutDimensions.center} cy={donutDimensions.center} r={donutDimensions.innerRadius - 2} fill="#fff" />
                        <text x={donutDimensions.center} y={donutDimensions.center - 5} textAnchor="middle" fill="#888" fontSize="11" fontWeight="bold">YHTEENSÄ</text>
                        <text x={donutDimensions.center} y={donutDimensions.center + 18} textAnchor="middle" fill="#222" fontSize="22" fontWeight="bold">
                          {totalAttributed}
                        </text>
                      </svg>
                    </div>

                    {/* Interaktiivinen selite (Legend) */}
                    <div className={classes.DonutLegend}>
                      {donutSectors.map((sector) => {
                        const isHovered = hoveredAttributionKey === sector.key;
                        return (
                          <div 
                            key={sector.key} 
                            className={`${classes.LegendRow} ${isHovered ? classes.LegendRowActive : ""}`}
                            onMouseEnter={() => setHoveredAttributionKey(sector.key)}
                            onMouseLeave={() => setHoveredAttributionKey(null)}
                          >
                            <span className={classes.Dot} style={{ background: attributionColors[sector.key] }} />
                            <div className={classes.LegendText}>
                              <span className={classes.Label}>{attributionLabels[sector.key]}</span>
                              <span className={classes.Val}>{sector.value} kpl ({(sector.percentage * 100).toFixed(0)}%)</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className={classes.EmptyChartMessage}>
                    Ei attribuutiotietoja haetulta aikaväliltä.
                  </div>
                )}
              </div>

            </div>

            {/* 3. Käyttäjäaktiviteetin kehitys (Audit Trail logs) */}
            <div className={classes.FullWidthCard}>
              <h3>HankiX-käyttöaktiviteetin viikkokehitys (Audit Trail)</h3>
              <div className={classes.ActivityScrollContainer}>
                <table className={classes.ActivityTable}>
                  <thead>
                    <tr>
                      <th>Viikko</th>
                      <th>Luodut tarjouspyynnöt </th>
                      <th>Tarjouspyyntöjen muokkaukset & arkistoinnit</th>
                      <th>Palveluntarjoajien kirjautumiset</th>
                      <th>Mikromaksuostot</th>
                      <th>Uudet tarjoukset (Offer)</th>
                      <th>Yritysprofiilin muokkaukset</th>
                      <th className={classes.TotalCol}>Yhteensä</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...comboData].reverse().map((d, idx) => (
                      <tr key={idx}>
                        <td><strong>{d.weekLabel}</strong> <span className={classes.DateSpan}>({d.startDate})</span></td>
                        <td>{d.activityLeadsCreated > 0 ? d.activityLeadsCreated : "-"}</td>
                        <td>{d.activityLeadsUpdated > 0 ? d.activityLeadsUpdated : "-"}</td>
                        <td>{d.activityLogins > 0 ? d.activityLogins : "-"}</td>
                        <td>{d.activityLeadsOpened > 0 ? d.activityLeadsOpened : "-"}</td>
                        <td>{d.activityOffersSaved > 0 ? d.activityOffersSaved : "-"}</td>
                        <td>{d.activityProfileUpdates > 0 ? d.activityProfileUpdates : "-"}</td>
                        <td className={classes.TotalCol}><strong>{d.totalActivity}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 4. Yrittäjien ansaintakehitys (MONTH / MICRO stacked) - Vain Primary/Operator */}
            {!isServiceProvider && (
              <div className={classes.FullWidthCard} style={{ marginTop: "2rem" }}>
                <h3>Aktiivisten kumppaniyritysten (palveluntarjoajien) ansaintamallijakauma</h3>
                <p style={{ color: "#666", marginBottom: "1.5rem" }}>
                  Tässä kaaviossa kuvataan, miten kiinteää kuukausimaksua maksavien (`MONTH`) ja mikromaksupohjaisten (`MICRO`) palveluntarjoajien määrä on kehittynyt valitulla ajanjaksolla.
                </p>
                <div className={classes.SvgWrapper}>
                  <svg 
                    viewBox={`0 0 ${providerChartDimensions.width} ${providerChartDimensions.height}`}
                    className={classes.InteractiveSvg}
                  >
                    <defs>
                      <linearGradient id="subBarGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity="0.85" />
                        <stop offset="100%" stopColor="#10b981" stopOpacity="0.3" />
                      </linearGradient>
                      <linearGradient id="microBarGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.85" />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.3" />
                      </linearGradient>
                    </defs>

                    {/* Taustaviivat */}
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
                      const y = providerChartDimensions.paddingTop + 
                        (providerChartDimensions.height - providerChartDimensions.paddingTop - providerChartDimensions.paddingBottom) * ratio;
                      return (
                        <line 
                          key={index}
                          x1={providerChartDimensions.paddingLeft}
                          y1={y}
                          x2={providerChartDimensions.width - providerChartDimensions.paddingRight}
                          y2={y}
                          stroke="rgba(255,255,255,0.06)"
                          strokeWidth="1"
                        />
                      );
                    })}

                    {/* Stacked Bars */}
                    {comboData.map((d, index) => {
                      const totalWeeks = comboData.length;
                      const chartWidth = providerChartDimensions.width - providerChartDimensions.paddingLeft - providerChartDimensions.paddingRight;
                      const colWidth = chartWidth / totalWeeks;
                      
                      const x = providerChartDimensions.paddingLeft + colWidth * index + (colWidth * 0.2);
                      const barWidth = colWidth * 0.6;
                      
                      const chartHeight = providerChartDimensions.height - providerChartDimensions.paddingTop - providerChartDimensions.paddingBottom;
                      
                      const subHeight = (d.subscriptionProviders / maxProviders) * chartHeight;
                      const microHeight = (d.microProviders / maxProviders) * chartHeight;

                      const subY = providerChartDimensions.height - providerChartDimensions.paddingBottom - subHeight;
                      const microY = subY - microHeight;
                      const totalProviders = d.subscriptionProviders + d.microProviders;

                      return (
                        <g 
                          key={index}
                          onMouseEnter={() => setHoveredProviderIndex(index)}
                          onMouseLeave={() => setHoveredProviderIndex(null)}
                          style={{ cursor: "pointer" }}
                        >
                          {/* Subscription part */}
                          {subHeight > 0 && (
                            <rect 
                              x={x}
                              y={subY}
                              width={barWidth}
                              height={subHeight}
                              fill="url(#subBarGrad)"
                              rx={subHeight > 3 ? 2 : 0}
                              style={{ 
                                fillOpacity: hoveredProviderIndex === index ? 1 : 0.85,
                                transition: "all 0.3s ease"
                              }}
                            />
                          )}
                          {/* Micro part */}
                          {microHeight > 0 && (
                            <rect 
                              x={x}
                              y={microY}
                              width={barWidth}
                              height={microHeight}
                              fill="url(#microBarGrad)"
                              rx={microHeight > 3 ? 2 : 0}
                              style={{ 
                                fillOpacity: hoveredProviderIndex === index ? 1 : 0.85,
                                transition: "all 0.3s ease"
                              }}
                            />
                          )}
                          {/* Kokonaismäärä palkin päällä */}
                          {totalProviders > 0 && (
                            <text
                              x={x + barWidth / 2}
                              y={microY - 6}
                              textAnchor="middle"
                              fill="#475569"
                              fontSize="10"
                              fontWeight="bold"
                            >
                              {totalProviders}
                            </text>
                          )}
                          {/* Osuuksien määrät palkkien sisällä */}
                          {subHeight > 14 && (
                            <text
                              x={x + barWidth / 2}
                              y={subY + subHeight / 2 + 4}
                              textAnchor="middle"
                              fill="#fff"
                              fontSize="9"
                              fontWeight="bold"
                            >
                              {d.subscriptionProviders}
                            </text>
                          )}
                          {microHeight > 14 && (
                            <text
                              x={x + barWidth / 2}
                              y={microY + microHeight / 2 + 4}
                              textAnchor="middle"
                              fill="#fff"
                              fontSize="9"
                              fontWeight="bold"
                            >
                              {d.microProviders}
                            </text>
                          )}
                          {/* Viikon akselinteksti */}
                          {index % (totalWeeks > 15 ? 2 : 1) === 0 && (
                            <text 
                              x={x + barWidth/2}
                              y={providerChartDimensions.height - 15}
                              textAnchor="middle"
                              fill="#888"
                              fontSize="11"
                            >
                              {d.weekLabel}
                            </text>
                          )}
                        </g>
                      );
                    })}

                    <text x={10} y={providerChartDimensions.paddingTop + 10} fill="#888" fontSize="10" fontWeight="bold">Yritykset kpl</text>
                  </svg>
                </div>

                {/* Siisti, dynaaminen Tooltip/Tietolaatikko palveluntarjoajien ansaintamallijakaumalle */}
                <div className={classes.ChartTooltipBox}>
                  {hoveredProviderIndex !== null ? (
                    <div className={classes.TooltipContent}>
                      <span className={classes.TooltipWeek}>
                        {comboData[hoveredProviderIndex].weekLabel} ({comboData[hoveredProviderIndex].startDate})
                      </span>
                      <div className={classes.TooltipRows}>
                        <div>
                          <strong>Yhteensä:</strong>
                          <span style={{ color: "#0f172a", fontWeight: "bold" }}>
                            {comboData[hoveredProviderIndex].subscriptionProviders + comboData[hoveredProviderIndex].microProviders} yritystä
                          </span>
                        </div>
                        <div>
                          <strong>Kk-maksu (MONTH):</strong>
                          <span style={{ color: "#10b981", fontWeight: "bold" }}>
                            {comboData[hoveredProviderIndex].subscriptionProviders} kpl ({((comboData[hoveredProviderIndex].subscriptionProviders / Math.max(comboData[hoveredProviderIndex].subscriptionProviders + comboData[hoveredProviderIndex].microProviders, 1)) * 100).toFixed(0)}%)
                          </span>
                        </div>
                        <div>
                          <strong>Mikromaksu (MICRO):</strong>
                          <span style={{ color: "#3b82f6", fontWeight: "bold" }}>
                            {comboData[hoveredProviderIndex].microProviders} kpl ({((comboData[hoveredProviderIndex].microProviders / Math.max(comboData[hoveredProviderIndex].subscriptionProviders + comboData[hoveredProviderIndex].microProviders, 1)) * 100).toFixed(0)}%)
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={classes.TooltipPlaceholder}>
                      Vie hiiri kuvaajan palkkien päälle nähdäksesi viikkokohtaiset määrät ja suhteet.
                    </div>
                  )}
                </div>
                
                {/* Selite legend */}
                <div style={{ display: "flex", gap: "2rem", justifyContent: "center", marginTop: "1rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <span style={{ display: "inline-block", width: "12px", height: "12px", borderRadius: "2px", background: "#10b981" }} />
                    <span style={{ fontSize: "0.9rem", color: "#555" }}>Kuukausilaskutus (`MONTH`)</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <span style={{ display: "inline-block", width: "12px", height: "12px", borderRadius: "2px", background: "#3b82f6" }} />
                    <span style={{ fontSize: "0.9rem", color: "#555" }}>Mikromaksu (`MICRO`)</span>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "5rem 0", color: "#777" }}>
            Valitse palvelu ladataksesi raportointi- ja tilastotiedot.
          </div>
        )}
      </div>
    </>
  );
};

export default Reports;
